import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { mapPinOutline, rothysSignetBadge } from '@dna/paths'
import styles from '@dna/styles'
import {
  debounce,
  getHaversineDistance,
  notify,
  t,
  updateStoreHours,
} from '@dna/utils'
import { LitElement, css, html, nothing, repeat, when } from '@lit'

class DNAStoreLocator extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    activeFilter: {
      type: String,
    },
    mapRequested: {
      type: Boolean,
    },
    resultsViewMode: {
      type: String,
    },
    searchRadius: {
      type: Number,
      attribute: 'search-radius',
    },
    searchSuggestions: {
      type: Array,
    },
    showSearchSuggestions: {
      type: Boolean,
    },
    storeFilteringOptions: {
      type: Array,
    },
    storeSearchResults: {
      type: Array,
    },
    userLocation: {
      type: Object,
    },
  }

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.dataReady = false
    this.activeFilter = 'all'
    this.autocompleteRequest = {
      input: '',
    }
    this.geolocationRequested = false
    this.hasGeolocated = false
    this.hasSearched = false
    this.mapRequested = false
    this.resultsViewMode = 'list'
    this.searchInputPlaceholder = t(
      'templates.stores.locator.search_placeholder',
    )
    this.searchRequested = false
    this.searchSuggestions = []
    this.selectedSearchSuggestion = ''
    this.showFilters = false
    this.showSearchSuggestions = false
    this.storeSearchResults = []
    this.storeFilteringOptions = [
      {
        id: 1,
        name: 'all',
        label: t('templates.stores.locator.filters.options.options__1_html'),
      },
      {
        id: 2,
        name: 'store',
        label: t('templates.stores.locator.filters.options.options__2_html'),
      },
      {
        id: 3,
        name: 'retailer',
        label: t('templates.stores.locator.filters.options.options__3_html'),
      },
    ]
    this.storeSearchTerm = ''
    this.userLocation = {}
    this.locationsWholesale = []
    this.locationsNotWholesale = []
    this.storePageUrls = []

    document.addEventListener('dna:stores:ready', (event) => {
      this.locationsWholesale = event.detail?.wholesale_locations
      this.locationsNotWholesale = event.detail?.locations
      this.locations = [
        ...this.locationsNotWholesale,
        ...this.locationsWholesale,
      ]
      this.locationsCount = this.locations.length
      this.dataReady = true
    })
  }

  connectedCallback() {
    super.connectedCallback()
    ;(async () => {
      const { AutocompleteSessionToken, AutocompleteSuggestion } =
        await google.maps.importLibrary('places')
      this.AutocompleteSessionToken = AutocompleteSessionToken
      this.AutocompleteSuggestion = AutocompleteSuggestion
      this._refreshAutocompleteSession(this.autocompleteRequest)
    })()
  }

  _refreshAutocompleteSession(request) {
    const token = new this.AutocompleteSessionToken()
    request.sessionToken = token
    return request
  }

  _useCurrentLocation() {
    if (this.dataReady) {
      this.geolocationRequested = true
      if (!this.mapRequested) {
        this.mapRequested = true
        if (window.sessionStorage.getItem('rothys_location')) {
          this.userLocation = JSON.parse(
            window.sessionStorage.getItem('rothys_location'),
          )
          this._getNearbyStores('geolocate')
          this.hasGeolocated = true
        } else if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              this.userLocation.latitude = position.coords.latitude
              this.userLocation.longitude = position.coords.longitude
              window.sessionStorage.setItem(
                'rothys_location',
                JSON.stringify(this.userLocation),
              )
              this._getNearbyStores('geolocate')
              this.hasGeolocated = true
              this.geolocationRequested = false
            },
            (error) => {
              this.hasGeolocated = false
              if (error.code === 1) {
                notify(
                  t('templates.stores.locator.geolocation_permission_denied'),
                  'warning',
                  5000,
                )
              }
              if (error.code === 2 || error.code === 3) {
                notify(
                  t('templates.stores.locator.geolocation_failed'),
                  'warning',
                  5000,
                )
              }
            },
          )
        }
      }
    }
  }

  async _useSearchLocation(event) {
    if (this.dataReady) {
      this.searchRequested = true
      this.$searchInput = event.target.renderRoot.querySelector('sl-input')

      if (!this.$searchInput.validity.tooShort) {
        this.storeSearchTerm = this.$searchInput.value
        if (this.storeSearchTerm.length) {
          this.autocompleteRequest.input = this.storeSearchTerm
          const { suggestions } =
            await this.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              this.autocompleteRequest,
            )
          this.searchSuggestions = suggestions
          if (this.searchSuggestions.length) {
            this.showSearchSuggestions = true
            // Dismiss search suggestions when clicking anywhere
            document.addEventListener('click', (event) => {
              if (!event.target.closest('.store-locator')) {
                this.showSearchSuggestions = false
              }
            })
          } else {
            this.showSearchSuggestions = false
          }
        } else {
          this.locationsCount = this.locations.length
          this._sendListUpdateEvent([], 'search cleared')
        }
      }
      this.hasSearched = true
      this.searchRequested = false
    }
  }

  _getNearbyStores(requestType) {
    try {
      this.storeSearchResults = []

      this.locations.map((location) => {
        let storeLocation
        let userLocation
        let updatedLocation = location

        if (location.fields.store_latitude && location.fields.store_longitude) {
          const storeLocationLatitude =
            typeof location.fields.store_latitude === 'number'
              ? location.fields.store_latitude
              : Number(location.fields.store_latitude)
          const storeLocationLongitude =
            typeof location.fields.store_longitude === 'number'
              ? location.fields.store_longitude
              : Number(location.fields.store_longitude)
          storeLocation = {
            latitude: storeLocationLatitude,
            longitude: storeLocationLongitude,
          }
        }

        if (this.userLocation.latitude && this.userLocation.longitude) {
          userLocation = this.userLocation
        }

        if (
          storeLocation?.latitude &&
          storeLocation?.longitude &&
          userLocation?.latitude &&
          userLocation?.longitude
        ) {
          const distance = getHaversineDistance(userLocation, storeLocation)
          if (distance <= this.searchRadius) {
            if (location.type !== 'wholesale_stores') {
              updatedLocation = updateStoreHours(location) || location
            }
            updatedLocation.distance_from_user = Math.ceil(Math.abs(distance))
            this.storeSearchResults.push(updatedLocation)
          }
        }
        return updatedLocation
      })
    } catch (error) {
      console.warn(
        'There was a problem calculating the distance from the requested location to store locations.',
      )
      console.log(error.message)
    }

    this.mapRequested = false
    this.locationsCount = this.storeSearchResults.length
    this.preFilteredResults = this.storeSearchResults
    this.showFilters = this.locationsCount > 0 || false

    if (requestType === 'geolocate') {
      this.searchInputPlaceholder = `${this.userLocation.latitude}, ${this.userLocation.longitude}`
    } else {
      this.searchInputPlaceholder = `${this.selectedSearchSuggestion.substring(0, 50)}`
    }

    // @segment
    Segment.protect(() => {
      const eventName =
        requestType === 'geolocate'
          ? Segment.CONSTANTS.EVENTS.STORES_GEOLOCATED
          : Segment.CONSTANTS.EVENTS.STORES_SEARCHED
      Segment.track(eventName, {
        store_search_query: this.hasSearched ? this.storeSearchTerm : undefined,
        store_search_suggestion: this.hasSearched
          ? this.selectedSearchSuggestion
          : undefined,
        store_locations_count: this.locationsCount,
      })
    })

    this._handleResultsFilter(false, 'all')
  }

  _sendListUpdateEvent(payload, status) {
    if (status === 'search cleared') {
      this.hasSearched = false
      this.hasGeolocated = false
      this.showFilters = false
      this.showSearchSuggestions = false
      this.selectedSearchSuggestion = ''
      this.searchInputPlaceholder = t(
        'templates.stores.locator.search_placeholder',
      )
    }

    const eventDetail = {
      current_location: this.userLocation,
      stores: [],
    }

    if (payload?.length) {
      eventDetail.stores = payload
    } else {
      this.storeSearchResults = []
    }

    if (status) eventDetail.status = status

    document.dispatchEvent(
      new CustomEvent('dna:store-locator:list-update', {
        bubbles: true,
        detail: eventDetail,
      }),
    )
  }

  _handleResultsFilter(event, filter) {
    this.activeFilter = event?.target?.value || filter

    this.storeSearchResults = this.preFilteredResults.filter((store) => {
      if (this.activeFilter === 'all') return true
      if (this.activeFilter === 'store')
        return store.type !== 'wholesale_stores'
      if (this.activeFilter === 'retailer')
        return store.type === 'wholesale_stores'
    })

    this._sendListUpdateEvent(this.storeSearchResults, 'results filtered')

    if (event) {
      // @segment
      Segment.protect(() => {
        Segment.track(Segment.CONSTANTS.EVENTS.STORES_FILTER_CLICKED, {
          store_location_type: this.activeFilter,
          store_locations_count: this.storeSearchResults.length,
        })
      })
    }
  }

  async _handleSearchSuggestionClick(suggestion) {
    this.showSearchSuggestions = false
    const placePrediction = suggestion.placePrediction.toPlace()
    if (!this.mapRequested) {
      this.mapRequested = true

      // Get place information from autocomplete suggestion
      await placePrediction.fetchFields({
        fields: ['formattedAddress', 'location'],
      })
      this._refreshAutocompleteSession(this.autocompleteRequest)

      // Update data
      this.selectedSearchSuggestion = `${placePrediction.formattedAddress}`
      this.userLocation.latitude = placePrediction.location.lat()
      this.userLocation.longitude = placePrediction.location.lng()
      this._getNearbyStores('search')
    }
  }

  _toggleResultsView(event, type) {
    document.dispatchEvent(
      new CustomEvent('dna:store-locator:list-view-toggled', {
        detail: { view_type: type },
      }),
    )
    this.resultsViewMode = type
  }

  _renderResultsFilter() {
    return html`
      <sl-radio-group
        label="${t('templates.stores.locator.filters.label')}"
        name="store-results-filter"
        value="${this.activeFilter}"
        class="store-results__filter w100${
          (this.hasGeolocated || this.hasSearched) && this.showFilters
            ? ''
            : ' hidden'
        }"
      >
        ${repeat(
          this.storeFilteringOptions,
          (option) => option.id,
          (option) => html`
            <sl-radio
              value="${option.name}"
              ?active=${this.activeFilter === option.name}
              @click=${this._handleResultsFilter}
            >
              ${when(
                option.name === 'store',
                () => html`
                  <div class="store-results__filter-option flex flex-align-center">
                    ${rothysSignetBadge}
                    ${option.label}
                  </div>
                `,
                () => option.label,
              )}
            </sl-radio>
          `,
        )}
      </sl-radio-group>
    `
  }

  _renderResultsCount = () => {
    return html`
      <p class="store-results__count p2 w100 flex flex-space-between ${this.storeSearchResults.length ? '' : ' hidden'}">
        <span>${this.storeSearchResults.length} ${t('templates.stores.name').toLowerCase()}</span>
        ${when(
          this.isMobile,
          () => html`
            <span class="store-results__view flex flex-space-between">
              <button
                ?active=${this.resultsViewMode === 'list'}
                @click=${(e) => this._toggleResultsView(e, 'list')}
              >
                ${t('templates.stores.locator.show_list')}
              </button>
              <span>  |  </span>
              <button
                ?active=${this.resultsViewMode === 'map'}
                @click=${(e) => this._toggleResultsView(e, 'map')}
              >
                ${t('templates.stores.locator.show_map')}
              </button>
            </span>
          `,
          () => nothing,
        )}
      </p>
    `
  }

  render() {
    return html`
      <div class="relative">
        <div class="store-locator__input">
          <sl-input
            aria-label="${t('templates.stores.locator.accessible_search_label')}"
            placeholder="${t('templates.stores.locator.search_label')}"
            size="large"
            type="search"
            minlength="3"
            autocomplete="off"
            value=${this.searchInputPlaceholder}
            @sl-input=${debounce((e) => {
              this._useSearchLocation(e)
            }, 250)}
            @sl-focus=${(e) => {
              e.target.style.border = '2px solid var(--dna-brand-primary)'
            }}
            @sl-blur=${(e) => {
              e.target.style.border = '2px solid transparent'
            }}
          >
            <sl-icon
              slot="suffix"
              name="x-lg"
              label="${t('templates.stores.locator.clear_search')}"
              ?active=${
                (this.storeSearchResults.length && this.hasSearched) ||
                (this.storeSearchResults.length && this.hasGeolocated)
              }
              tabindex=${
                (this.storeSearchResults.length && this.hasSearched) ||
                (this.storeSearchResults.length && this.hasGeolocated)
                  ? '0'
                  : ''
              }
              @click=${() => this._sendListUpdateEvent([], 'search cleared')}
            ></sl-icon>
          </sl-input>
        </div>
        <div
          class="store-locator__suggestions"
          ?active=${this.showSearchSuggestions}
        >
          ${when(
            this.showSearchSuggestions,
            () => html`
              <ul class="flex flex-column">
                ${repeat(
                  this.searchSuggestions,
                  (suggestion) => suggestion.placePrediction.placeId,
                  (suggestion) => html`
                    <li>
                      <button
                        @click=${() => this._handleSearchSuggestionClick(suggestion)}
                      >
                        ${suggestion.placePrediction.text.toString()}
                      </button>
                    </li>
                  `,
                )}
              </ul>
            `,
            () => nothing,
          )}
        </div>
      </div>
      <div
        class="store-locator__geolocation flex w100${this.showSearchSuggestions ? ' hidden' : ''}"
        tabindex="0"
      >
        ${mapPinOutline}
        <button
          class="p3 pl-2 c-midnight"
          @click=${this._useCurrentLocation}
        >
          ${
            this.geolocationRequested
              ? t('templates.stores.locator.geolocating')
              : t('templates.stores.locator.geolocate')
          }
        </button>
      </div>
      ${this._renderResultsFilter()}
      ${this._renderResultsCount()}
    `
  }

  static styles = [
    styles.a11y,
    styles.flex,
    styles.position,
    styles.reset,
    styles.sizing,
    styles.spacing,
    styles.typography,
    css`
      .store-locator__input {
        position: relative;
        border: none !important;
        max-width: 636px;
        min-height: unset !important;
        width: 100%;
      }
      .store-locator__input sl-input {
        border: 2px solid transparent;
        margin-bottom: var(--dna-space-4);
        width: 100%;
        --sl-input-focus-ring-color: var(--dna-brand-primary);
      }
      .store-locator__input sl-input sl-icon {
        color: var(--dna-brand-primary);
        height: var(--dna-space-4);
        width: var(--dna-space-4);
        margin-inline-end: var(--dna-space-4);
        opacity: 0;
        transition: opacity .2s ease;
      }
      .store-locator__input sl-input sl-icon[active] {
        cursor: pointer;
        opacity: 1;
      }
      .store-locator__input sl-input::part(base) {
        border-radius: 0;
        border: 1px solid var(--dna-color-light-grey);
        box-shadow: none !important;
      }
      .store-locator__input sl-input::part(input) {
        color: var(--dna-color-midnight);
        font: var(--dna-body-p1-font);
        padding: var(--dna-space-3) var(--dna-space-4);
      }
      .store-locator__input sl-input::part(input)::placeholder {
        color: var(--dna-color-midnight);
      }
      .store-locator__suggestions {
        border-top: none;
        position: absolute;
        top: calc(100% - var(--dna-space-5));
        z-index: 1;
      }
      .store-locator__suggestions[active] {
        display: flex;
      }
      .store-locator__suggestions ul {
        gap: var(--dna-space-4);
        background-color: var(--dna-brand-reverse);
        border: 1px solid var(--dna-color-light-grey) !important;
        box-shadow: 0px calc(var(--dna-space-5) / 4) calc(var(--dna-space-5) / 2) rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
        font: var(--dna-body-p2-font);
        list-style: none;
        margin: 0;
        padding: var(--dna-space-4);
        width: 100%;
      }
      .store-locator__suggestions button {
        font: var(--dna-body-p1-font);
        text-align: left;
      }
      .store-locator__suggestions button:focus {
        outline: 2px solid var(--dna-brand-primary);
      }
      ::slotted(.store-locator__geolocation) {
        cursor: pointer;
        font: var(--dna-body-p3-font);
        text-decoration: underline;
        display: flex;
        align-items: center;
      }
      .store-results__view {
        gap: var(--dna-space-3);

        button {
          font: unset;
        }
        button[active] {
          color: var(--dna-color-reflex-blue);
        }
      }
      .store-results__filter {
        margin: 0;
        margin-top: var(--dna-space-6);
        margin-bottom: calc(var(--dna-space-5) + var(--dna-space-2));
        padding: 0;

        sl-radio .store-results__filter-option {
          gap: calc(var(--dna-space-3) / 2);

          svg {
            aspect-ratio: 1;
            width: var(--dna-space-5);
          }
        }
        sl-radio::part(base) {
          align-items: center;
        }
        sl-radio::part(label) {
          font: var(--dna-body-p2-font);
          line-height: 14px;
          margin-inline-start: var(--dna-space-2);
        }
        sl-radio::part(control) {
          aspect-ratio: 1;
          height: unset;
          width: var(--dna-space-4);
        }
        sl-radio::part(control--checked) {
          outline: 2px solid var(--dna-brand-reverse);
          outline-offset: calc(3px * -1);
        }
        sl-radio::part(control--checked):hover {
          background-color: var(--dna-brand-primary);
        }
        sl-radio::part(control):hover {
          border-color: var(--dna-brand-primary);
        }
        sl-radio::part(checked-icon) {
          display: none;
        }
      }
      .store-results__filter::part(form-control-label) {
        font: var(--dna-label-m-font);
        letter-spacing: unset;
        margin-bottom: var(--dna-space-3);
      }
      .store-results__filter::part(form-control-input) {
        display: flex;
        align-items: center;
        gap: var(--dna-space-4);
      }
      .store-results__filter-option--with-icon > div {
        height: 100%;
      }
      .store-results__filter-option-icon {
        aspect-ratio: 1;
        margin-right: var(--dna-space-1);
        width: var(--dna-space-4);

        svg {
          height: 100%;
          width: 100%;
        }
      }
      .store-results__count {
        margin-bottom: calc(var(--dna-space-5) + var(--dna-space-2));
      }
      @media screen and (min-width: 768px) {
        .store-locator__input sl-input {
          margin-bottom: var(--dna-space-3);
        }
        .store-results__filter {
          legend {
            margin-bottom: var(--dna-space-2);
          }
          sl-radio::part(label) {
            font: var(--dna-body-p3-font);
            line-height: 10px;
          }
        }
      }
      @media screen and (min-width: 1024px) {
        .store-results__count {
          margin-bottom: 0;
        }
        .store-locator__suggestions {
          top: calc(100% - var(--dna-space-4));
        }
        .store-results__filter {
          sl-radio .store-results__filter-option {
            gap: var(--dna-space-2);

            svg {
              aspect-ratio: 1;
              gap: var(--dna-space-2);
              width: var(--dna-space-4);
            }
          }
        }
      }
    `,
  ]
}
customElements.define('dna-store-locator', DNAStoreLocator)
